import React, { useState, useMemo, useCallback } from 'react';
import { Table, Input, Tag, Button, Tooltip, Timeline } from 'antd';
import {
  SearchOutlined, PauseCircleOutlined, StopOutlined,
  ClockCircleOutlined, SendOutlined, CheckCircleOutlined,
  EyeOutlined, MessageOutlined, WarningOutlined,
  CaretRightOutlined,
} from '@ant-design/icons';
import EngagementBadge from './EngagementBadge';

const COLORS = {
  primary: '#1a1a2e',
  surface: '#f8fafc',
  surfaceLight: '#f1f5f9',
  border: '#e2e8f0',
  text: '#1a1a2e',
  textSecondary: '#475569',
  textMuted: '#94a3b8',
  emerald: '#10b981',
  emeraldBg: 'rgba(16,185,129,0.12)',
  amber: '#f59e0b',
  amberBg: 'rgba(245,158,11,0.12)',
  rose: '#ef4444',
  roseBg: 'rgba(239,68,68,0.12)',
  violet: '#8b5cf6',
  violetBg: 'rgba(139,92,246,0.12)',
  blue: '#3b82f6',
  blueBg: 'rgba(59,130,246,0.12)',
  glass: 'rgba(255,255,255,0.04)',
  glassBorder: 'rgba(255,255,255,0.08)',
};

const STATUS_TABS = [
  { key: 'all', label: 'All', icon: null },
  { key: 'queued', label: 'Queued', icon: <ClockCircleOutlined /> },
  { key: 'sent', label: 'Sent', icon: <SendOutlined /> },
  { key: 'delivered', label: 'Delivered', icon: <CheckCircleOutlined /> },
  { key: 'opened', label: 'Opened', icon: <EyeOutlined /> },
  { key: 'replied', label: 'Replied', icon: <MessageOutlined /> },
  { key: 'bounced', label: 'Bounced', icon: <WarningOutlined /> },
  { key: 'paused', label: 'Paused', icon: <PauseCircleOutlined /> },
  { key: 'stopped', label: 'Stopped', icon: <StopOutlined /> },
];

const STATUS_COLORS = {
  queued: { color: COLORS.textSecondary, bg: 'rgba(148,163,184,0.12)' },
  sent: { color: COLORS.blue, bg: COLORS.blueBg },
  delivered: { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)' },
  opened: { color: COLORS.emerald, bg: COLORS.emeraldBg },
  replied: { color: COLORS.violet, bg: COLORS.violetBg },
  bounced: { color: COLORS.rose, bg: COLORS.roseBg },
  paused: { color: COLORS.amber, bg: COLORS.amberBg },
  stopped: { color: COLORS.rose, bg: COLORS.roseBg },
};

const glassCard = {
  background: COLORS.glass,
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: `1px solid ${COLORS.glassBorder}`,
  borderRadius: 12,
};

const STEP_HISTORY_ICONS = {
  sent: <SendOutlined style={{ color: COLORS.blue }} />,
  delivered: <CheckCircleOutlined style={{ color: '#06b6d4' }} />,
  opened: <EyeOutlined style={{ color: COLORS.emerald }} />,
  clicked: <CaretRightOutlined style={{ color: COLORS.violet }} />,
  replied: <MessageOutlined style={{ color: COLORS.violet }} />,
  bounced: <WarningOutlined style={{ color: COLORS.rose }} />,
};

const RecipientTable = ({ recipients, onStatusChange, loading, steps }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchText, setSearchText] = useState('');

  const filteredRecipients = useMemo(() => {
    let data = recipients || [];
    if (activeTab !== 'all') {
      data = data.filter((r) => r.status === activeTab);
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      data = data.filter((r) =>
        (r.contactPerson || '').toLowerCase().includes(q) ||
        (r.email || '').toLowerCase().includes(q) ||
        (r.agencyName || r.leadName || '').toLowerCase().includes(q)
      );
    }
    return data;
  }, [recipients, activeTab, searchText]);

  const tabCounts = useMemo(() => {
    const counts = { all: (recipients || []).length };
    STATUS_TABS.forEach((t) => {
      if (t.key !== 'all') {
        counts[t.key] = (recipients || []).filter((r) => r.status === t.key).length;
      }
    });
    return counts;
  }, [recipients]);

  const handlePause = useCallback((recipientId) => {
    if (onStatusChange) onStatusChange(recipientId, 'paused');
  }, [onStatusChange]);

  const handleStop = useCallback((recipientId) => {
    if (onStatusChange) onStatusChange(recipientId, 'stopped');
  }, [onStatusChange]);

  const columns = [
    {
      title: 'Contact',
      dataIndex: 'contactName',
      key: 'contactName',
      width: 180,
      render: (text, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 600, color: COLORS.text, fontSize: 13 }}>
            {text || 'Unknown'}
          </span>
          <span style={{ fontSize: 11, color: COLORS.textMuted }}>
            {record.contactTitle || record.title || ''}
          </span>
        </div>
      ),
    },
    {
      title: 'Agency',
      dataIndex: 'agencyName',
      key: 'agencyName',
      width: 160,
      render: (text, record) => (
        <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>
          {text || record.leadName || '-'}
        </span>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'contactEmail',
      key: 'contactEmail',
      width: 220,
      render: (text) => (
        <span style={{ color: COLORS.textSecondary, fontSize: 12, fontFamily: 'monospace' }}>
          {text || '-'}
        </span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status, record) => {
        const config = STATUS_COLORS[status] || STATUS_COLORS.queued;
        const isProxy = record.stepHistory?.some(sh => sh.isProxy && sh.openedAt);
        return (
          <Tooltip title={isProxy ? 'Opened via Gmail proxy — likely a real open but unconfirmed' : ''}>
            <Tag
              style={{
                color: config.color,
                background: config.bg,
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 11,
                textTransform: 'capitalize',
            }}
          >
            {status || 'queued'}{isProxy ? ' 📡' : ''}
          </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Step',
      dataIndex: 'currentStep',
      key: 'currentStep',
      width: 70,
      align: 'center',
      render: (step) => (
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 26,
          height: 26,
          borderRadius: 7,
          background: COLORS.violetBg,
          color: COLORS.violet,
          fontSize: 12,
          fontWeight: 700,
        }}>
          {step || 1}
        </span>
      ),
    },
    {
      title: 'Engagement',
      dataIndex: 'engagementTag',
      key: 'engagementTag',
      width: 130,
      render: (tag) => <EngagementBadge tag={tag} />,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => {
        const isPauseable = ['queued', 'sent', 'delivered', 'opened'].includes(record.status);
        const isStoppable = record.status !== 'stopped';
        return (
          <div style={{ display: 'flex', gap: 4 }}>
            {isPauseable && (
              <Tooltip title={record.status === 'paused' ? 'Resume' : 'Pause'}>
                <Button
                  type="text"
                  size="small"
                  icon={<PauseCircleOutlined />}
                  onClick={() => handlePause(record._id || record.key)}
                  style={{ color: COLORS.amber }}
                />
              </Tooltip>
            )}
            {isStoppable && (
              <Tooltip title="Stop">
                <Button
                  type="text"
                  size="small"
                  icon={<StopOutlined />}
                  onClick={() => handleStop(record._id || record.key)}
                  style={{ color: COLORS.rose }}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  const expandedRowRender = (record) => {
    const history = record.stepHistory || [];
    // Build timeline: completed steps from stepHistory + upcoming steps from campaign
    const allSteps = [];

    // Add completed/sent steps
    history.forEach((sh) => {
      const statusIcon = sh.repliedAt ? <MessageOutlined style={{ color: COLORS.violet }} />
        : sh.openedAt ? <EyeOutlined style={{ color: COLORS.blue }} />
        : sh.sentAt ? <CheckCircleOutlined style={{ color: COLORS.emerald }} />
        : <ClockCircleOutlined style={{ color: COLORS.textMuted }} />;

      const isProxyOpen = sh.isProxy || false;
      const statusLabel = sh.repliedAt ? 'Replied' : sh.openedAt ? (isProxyOpen ? 'Opened (via proxy)' : 'Opened') : sh.status === 'delivered' ? 'Delivered' : sh.sentAt ? 'Sent' : 'Pending';
      const statusColor = sh.repliedAt ? COLORS.violet : sh.openedAt ? COLORS.blue : sh.status === 'delivered' ? COLORS.emerald : sh.sentAt ? COLORS.emerald : COLORS.textMuted;

      allSteps.push({
        stepNumber: sh.stepNumber,
        dot: statusIcon,
        color: statusColor,
        children: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag color={statusColor} style={{ fontSize: 11, borderRadius: 4, margin: 0 }}>{statusLabel}</Tag>
              <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>Step {sh.stepNumber}</span>
            </div>
            <div style={{ fontSize: 11, color: COLORS.textSecondary, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {sh.sentAt && <span>Sent: {new Date(sh.sentAt).toLocaleString()}</span>}
              {sh.openedAt && <span>Opened: {new Date(sh.openedAt).toLocaleString()}</span>}
              {sh.clickedAt && <span>Clicked: {new Date(sh.clickedAt).toLocaleString()}</span>}
              {sh.repliedAt && <span>Replied: {new Date(sh.repliedAt).toLocaleString()}</span>}
              {sh.bouncedAt && <span style={{ color: COLORS.rose }}>Bounced: {new Date(sh.bouncedAt).toLocaleString()}</span>}
            </div>
          </div>
        ),
      });
    });

    // Add upcoming steps (steps not yet in stepHistory)
    const sentStepNumbers = new Set(history.map(sh => sh.stepNumber));
    const campaignSteps = steps || [];
    campaignSteps.forEach((step) => {
      if (sentStepNumbers.has(step.stepNumber)) return; // already shown
      if (['replied', 'bounced', 'complained', 'unsubscribed', 'stopped'].includes(record.status)) return; // won't receive more

      // Calculate estimated send date
      let estimatedDate = '';
      if (history.length > 0 && step.delayDays) {
        const lastSent = history[history.length - 1]?.sentAt;
        if (lastSent) {
          const est = new Date(new Date(lastSent).getTime() + (step.delayDays * 24 + (step.delayHours || 0)) * 60 * 60 * 1000);
          estimatedDate = est.toLocaleString();
        }
      }

      allSteps.push({
        stepNumber: step.stepNumber,
        dot: <ClockCircleOutlined style={{ color: COLORS.amber }} />,
        color: COLORS.amber,
        children: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Tag color={COLORS.amber} style={{ fontSize: 11, borderRadius: 4, margin: 0 }}>Upcoming</Tag>
              <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>Step {step.stepNumber}</span>
              <span style={{ fontSize: 11, color: COLORS.textMuted }}>({step.templateName || 'No template'})</span>
            </div>
            <div style={{ fontSize: 11, color: COLORS.textSecondary }}>
              <span>Delay: {step.delayDays || 0}d {step.delayHours || 0}h</span>
              <span style={{ margin: '0 8px' }}>|</span>
              <span>Targeting: {(step.targeting || 'all').replace(/_/g, ' ')}</span>
              {estimatedDate && <>
                <span style={{ margin: '0 8px' }}>|</span>
                <span>Est. send: {estimatedDate}</span>
              </>}
            </div>
          </div>
        ),
      });
    });

    if (allSteps.length === 0) {
      return (
        <div style={{ padding: '8px 16px', color: COLORS.textMuted, fontSize: 12 }}>
          No step history available yet
        </div>
      );
    }

    return (
      <div style={{ padding: '8px 24px' }}>
        <Timeline items={allSteps} />
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Status Filter Tabs */}
      <div style={{
        display: 'flex',
        gap: 4,
        overflowX: 'auto',
        padding: '4px 0',
      }}>
        {STATUS_TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          const count = tabCounts[tab.key] || 0;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 14px',
                borderRadius: 8,
                border: `1px solid ${isActive ? COLORS.violet : COLORS.glassBorder}`,
                background: isActive ? COLORS.violetBg : COLORS.glass,
                color: isActive ? COLORS.violet : COLORS.textSecondary,
                fontSize: 12,
                fontWeight: isActive ? 600 : 500,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s',
              }}
            >
              {tab.icon}
              {tab.label}
              <span style={{
                fontSize: 10,
                fontWeight: 700,
                padding: '1px 6px',
                borderRadius: 4,
                background: isActive ? 'rgba(139,92,246,0.2)' : 'rgba(255,255,255,0.06)',
                color: isActive ? COLORS.violet : COLORS.textMuted,
              }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <Input
        prefix={<SearchOutlined style={{ color: COLORS.textMuted }} />}
        placeholder="Search by contact name, email, or agency..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ maxWidth: 400 }}
        allowClear
      />

      {/* Table */}
      <div style={{ ...glassCard, padding: 0, overflow: 'hidden' }}>
        <Table
          columns={columns}
          dataSource={filteredRecipients}
          rowKey={(r) => r._id || r.key || r.email}
          loading={loading}
          pagination={{
            pageSize: 25,
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            showTotal: (total, range) => (
              <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>
                {range[0]}-{range[1]} of {total} recipients
              </span>
            ),
          }}
          expandable={{
            expandedRowRender,
            expandIcon: ({ expanded, onExpand, record }) => (
              <CaretRightOutlined
                onClick={(e) => onExpand(record, e)}
                style={{
                  color: COLORS.violet,
                  fontSize: 10,
                  transform: expanded ? 'rotate(90deg)' : 'rotate(0)',
                  transition: 'transform 0.2s',
                  cursor: 'pointer',
                }}
              />
            ),
          }}
          size="small"
          scroll={{ x: 900 }}
          locale={{
            emptyText: (
              <div style={{ padding: 32, color: COLORS.textMuted }}>
                No recipients found
              </div>
            ),
          }}
        />
      </div>
    </div>
  );
};

export default RecipientTable;
